import { useEffect, useState } from "react";
import { X, Heart, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { getVault, removeFromVault } from "@/lib/api";
import { getTrustBadge } from "@/features/snaggedify/dealFeedData";
import { SafeImg } from "@/components/SafeImg";
import { snagOutbound } from "@/lib/outbound";
import { toast } from "@/components/ui/sonner";

// Visual Vault grid — Side Drawer's History tab CTA opens this to browse every
// persisted save as an image-first grid (mirrors the Scroll card look), rather than
// just the plain text History log.
export default function VaultGridModal({ open, onClose }) {
  const [items, setItems] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    getVault().then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [open]);

  if (!open) return null;

  const remove = async (dealId) => {
    setRemovingId(dealId);
    try {
      await removeFromVault(dealId);
      setItems((prev) => prev.filter((it) => it.deal_id !== dealId));
      toast.success("Removed from Vault.");
    } catch {
      toast.error("Couldn't remove that item.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div id="vault-grid-modal" data-testid="vault-grid-modal" className="fixed inset-0 z-[180] flex items-end justify-center bg-black/70 backdrop-blur-md sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-[#121214] sm:rounded-3xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-wide text-slate-900 dark:text-white"><Heart className="h-4 w-4 text-brand-orange" /> Your Vault</h3>
          <button onClick={onClose} data-testid="vault-grid-close-btn" aria-label="Close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {items === null ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm font-bold text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading Vault…</div>
          ) : items.length === 0 ? (
            <p className="py-12 text-center text-xs font-bold text-slate-400" data-testid="vault-grid-empty">Nothing saved yet — heart a deal in Scroll or Discover to build your Vault.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="vault-grid">
              {items.map((it) => {
                const badge = getTrustBadge(it.verdict);
                return (
                  <div key={it.deal_id} data-testid={`vault-grid-item-${it.deal_id}`} className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-2.5 dark:border-white/10 dark:bg-white/5">
                    <div className="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-100 dark:bg-white/10">
                      {it.image ? <SafeImg src={it.image} alt={it.title} className="h-full w-full object-cover" /> : <ShieldCheck className="h-8 w-8 text-slate-300" />}
                    </div>
                    <span className={`trust-badge mt-2 inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-wide ${badge.className}`} data-testid={`vault-badge-${it.deal_id}`}>
                      <badge.Icon className="h-2.5 w-2.5" strokeWidth={3} /> {badge.label}
                    </span>
                    <p className="mt-1 line-clamp-2 text-[11px] font-bold text-slate-900 dark:text-white">{it.title}</p>
                    <p className="mt-0.5 text-xs font-black text-brand-orange dark:text-amber-400">${Number(it.price ?? 0).toFixed(2)}</p>
                    <div className="mt-2 flex gap-1.5">
                      <button
                        onClick={() => snagOutbound({ url: it.outbound_url, itemId: it.deal_id, merchant: it.reseller, title: it.title, price: it.price })}
                        data-testid={`vault-grid-snag-${it.deal_id}`}
                        className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-slate-900 py-1.5 text-[10px] font-black uppercase text-white dark:bg-white dark:text-slate-950"
                      >
                        <ExternalLink className="h-3 w-3" /> Snag
                      </button>
                      <button
                        onClick={() => remove(it.deal_id)}
                        disabled={removingId === it.deal_id}
                        data-testid={`vault-grid-remove-${it.deal_id}`}
                        className="flex items-center justify-center rounded-lg border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-rose-500 disabled:opacity-50"
                      >
                        {removingId === it.deal_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Heart className="h-3 w-3 fill-current" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
